<template>
	<div class="list-item nav-list-item" role="listitem"
		 @click.prevent.stop="sendRequest"
		 :class="[colorClass ? 'text-' + colorClass : '', sizeableString ? 'nav-list-item-' + sizeableString : '', roundableString]"
		 :style="{color: colorHex}">
		{{ text }}
		<slot/>
		<progress-bar v-if="isLoading" :color="colorHex" bottom absolute/>
	</div>
</template>

<script setup lang="ts">
import {SubmittableComponentEmits, type SubmittableComponentProps, useSubmittableComponent} from "../../../composables/useSubmittableComponent";
import {type NavListItemComponentProps} from "../../../types/list";
import {useRoundable} from "../../../composables/useRoundable";
import {useSizeable} from "../../../composables/useSizeable";
import {useColorable} from "../../../composables/useColorable";

const props = withDefaults(defineProps<NavListItemComponentProps & SubmittableComponentProps>(), {});

const emit = defineEmits([...SubmittableComponentEmits]);

const {submit, isLoading} = useSubmittableComponent(props, emit)
const {roundableString} = useRoundable(props);
const {sizeableString} = useSizeable(props);
const {colorClass, colorHex} = useColorable(props);

function sendRequest() {
	submit().catch(() => {
		// Ignore as already reported
	});
}

</script>

<style>
/*********************
  Inputs
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.app .list-item.nav-list-item {
  color: #465050;
  align-items: center;
  display: flex;
  letter-spacing: normal;
  outline: none;
  padding: 12px 16px;
  position: relative;
  cursor: pointer;
  font-size: 15px;
}
.app .list-item.nav-list-item.rounded-md {
  border-radius: 32px !important;
}
.app .list-item.nav-list-item:before {
  background-color: currentColor;
  border-radius: inherit;
  bottom: 0;
  content: "";
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
}
.app .list-item.nav-list-item:hover:before {
  opacity: 0.04;
}
.app .list-item.nav-list-item.list-item--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.app .list-item.nav-list-item.list-item--disabled:hover:before {
  opacity: 0;
}
.app .list-item.nav-list-item.list-item--active:before {
  background-color: #7D8484;
  opacity: 0.12;
}
.app .list-item.nav-list-item.list-item--active .active-border {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: #008200;
}
.app .list-item.nav-list-item.nav-list-item-xs {
  font-size: 13px;
  padding: 8px 16px;
}
.app .list-item.nav-list-item.nav-list-item-sm {
  font-size: 14px;
  padding: 10px 16px;
}
.app .list-item.nav-list-item.nav-list-item-lg {
  padding: 14px 16px;
}
</style>